import React, { useState, useEffect, useCallback } from 'react'
import { Card, Button, Avatar, List, Row, Col, message, Space, Typography, Skeleton, theme, Input } from 'antd'
import {
  ClockCircleOutlined,
  VideoCameraOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  UserOutlined,
  CalendarOutlined,
  PercentageOutlined,
  CloseCircleOutlined,
  PlayCircleOutlined
} from '@ant-design/icons'
import { useNotifications } from '../context/NotificationsContext'
import { useWebSocket } from '../context/WebSocketContext'
import NotificationsPopover from './NotificationsPopover'
import WebRTCAssistant from '../../../../P AI Voice Assistant/lib/components/WebRTCAssistant'
import LetterModal from './DashBoard AI/components/LetterModal'
import dayjs from 'dayjs'
import { useDispatch, useSelector } from 'react-redux'
import { executeBooking, getBookingState } from '../../../../Bookings/utils/bpm-utils'
import { updateAllBookingsState } from '../../../../lib/booking-utils'
import { updateBooking } from '../../../../Bookings/utils/utils'
import { readSystemUsersAggregate } from '../../../../Admin/system-users/utils/utils'
import { handleError } from '../../../../lib/utils'
import PatientExaminationForm from '../../../../Bookings/components/ExaminationWrapper'
import CoreEnums from '../../../../../core/utils/enums'
import coreReducer from '../../../../../core/utils/reducer'
// import bookingsReducer from '../../../../Bookings/utils/booking-reducer'
import { isDoctor, isNurse } from '../../../../lib/profile-utils'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSync, faUserCheck } from '@fortawesome/free-solid-svg-icons'
import { hexToRGBA } from '../../../../lib/utils'
import { generateStatus } from '../../../../lib/booking-utils'
import { FaCalendarTimes } from 'react-icons/fa'

const { Title, Text } = Typography

// Calculate age accurately from date of birth
const calculateAge = (dateOfBirth) => {
  if (!dateOfBirth) return 'Unknown'
  return `${dayjs().diff(dayjs(dateOfBirth), 'year')} yrs`
}

const METRIC_CONFIGS = {
  unclaimed: {
    icon: <UserOutlined />,
    label: 'Unclaimed',
    suffix: '',
    color: '#f5222d', // Red
    showDecimals: false,
    flicker: true
  },
  scheduled: {
    icon: <CalendarOutlined />,
    label: 'Scheduled',
    suffix: '',
    color: '#1890ff', // Blue
    showDecimals: false
  },
  nonScheduled: {
    icon: <FaCalendarTimes />,
    label: 'Non-Scheduled',
    suffix: '',
    color: '#f5222d', // Red
    showDecimals: false
  },
  today: {
    icon: <CalendarOutlined />,
    label: 'Today',
    suffix: '',
    color: '#1890ff', // Blue
    showDecimals: false
  },
  thisWeek: {
    icon: <CalendarOutlined />,
    label: 'This Week',
    suffix: '',
    color: '#1890ff', // Blue
    showDecimals: false
  },
  thisMonth: {
    icon: <CalendarOutlined />,
    label: 'This Month',
    suffix: '',
    color: '#1890ff', // Blue
    showDecimals: false
  },
  utilizationRate: {
    icon: <PercentageOutlined />,
    label: 'Utilization',
    suffix: '%',
    color: '#1890ff', // Blue
    showDecimals: true
  },
  cancellations: {
    icon: <CloseCircleOutlined />,
    label: 'Cancelled',
    suffix: '',
    color: '#f5222d', // Red
    showDecimals: false
  },
  virtualConsults: {
    icon: <VideoCameraOutlined />,
    label: 'Virtual',
    suffix: '%',
    color: '#1890ff', // Blue
    showDecimals: true
  }
}

const getStatusColor = (status) => {
  const colors = {
    RECEPTION: '#1890ff',
    'CHECK-IN': '#00bcd4',
    DIAGNOSIS: '#ffc107'
  }
  return colors[status] || '#1890ff'
}

// Get avatar background color based on gender
const getAvatarColor = (gender) => {
  if (!gender) return '#1890ff' // Default blue if gender unknown
  gender = gender.toLowerCase()
  return gender === 'female' ? '#E091A9' : '#4169E1' // Softer pink for female, Royal Blue for male
}

const AppointmentOverview = ({ refreshView, metrics, loadingMetrics, loadingApps, refreshMetrics }) => {
  const { isConnected, sendMessage } = useWebSocket()
  const { addNotification } = useNotifications()
  const dispatch = useDispatch()
  const state = useSelector((state) => state)
  const appointments = useSelector((state) => state.bookings.dashboardData)
  const [isModalVisible, setIsModalVisible] = useState(false)
  const [letterData, setLetterData] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [summaryData, setSummaryData] = useState({
    upcoming: []
  })

  // Listen for WebSocket messages related to appointments
  useEffect(() => {
    const handleWebSocketMessage = (event) => {
      const { type, action, data } = event.detail

      if (type === 'BOOKING') {
        // Refresh the metrics and view to get the latest data
        refreshMetrics()
        refreshView()

        // Show notification about the booking change
        const actionText = action.toLowerCase()
        addNotification({
          title: `Booking ${actionText}`,
          description: `A booking has been ${actionText}ed`,
          type: 'info'
        })
      }
    }

    window.addEventListener('websocket-message', handleWebSocketMessage)

    return () => {
      window.removeEventListener('websocket-message', handleWebSocketMessage)
    }
  }, [refreshMetrics, refreshView, addNotification])

  // Process appointments when component mounts or appointments change
  useEffect(() => {
    // Filter today's appointments and handle missing data
    const upcoming = appointments
      .filter((apt) => {
        // Handle missing bookingDate
        if (!apt.bookingDate) {
          return true // Include appointments without bookingDate
        }

        const aptDate = dayjs(apt.bookingDate)
        const isToday = aptDate.isSame(dayjs(), 'day')

        // Apply search filter if searchTerm exists
        if (searchTerm) {
          const searchQuery = searchTerm.toLowerCase()
          const patientName = (
            apt.patientRecord?.firstName && apt.patientRecord?.lastName
              ? `${apt.patientRecord.firstName} ${apt.patientRecord.lastName}`
              : apt.patientName || 'Unknown Patient'
          ).toLowerCase()
          const medicalProfName = apt.medicalProfName?.toLowerCase() || ''
          return isToday && (patientName.includes(searchQuery) || medicalProfName.includes(searchQuery))
        }

        return isToday
      })
      .sort((a, b) => {
        // Handle missing bookingDate in sorting
        if (!a.bookingDate) return -1 // Put appointments without date at the start
        if (!b.bookingDate) return 1

        // First compare dates
        const dateA = dayjs(a.bookingDate)
        const dateB = dayjs(b.bookingDate)
        const dateDiff = dateA.diff(dateB)

        // If same date, compare times
        if (dateDiff === 0) {
          // Handle missing startTime
          if (!a.startTime) return -1
          if (!b.startTime) return 1

          const timeA = a.startTime.split(':').map(Number)
          const timeB = b.startTime.split(':').map(Number)
          return timeA[0] * 60 + timeA[1] - (timeB[0] * 60 + timeB[1])
        }

        return dateDiff
      })

    // Update summary data
    setSummaryData({
      upcoming
    })
  }, [appointments, searchTerm])

  const renderTrendIndicator = (trend, percent) => {
    // Convert to number to ensure proper comparison
    const numericPercent = parseFloat(percent)

    // If 0% change, show a horizontal dash in yellow
    if (numericPercent === 0) {
      return <Text style={{ color: '#faad14' }}>— 0%</Text>
    }

    // Use green for positive change, red for negative change
    const color = trend === 'up' ? '#52c41a' : '#ff4d4f'

    return (
      <Space>
        {trend === 'up' ? <ArrowUpOutlined style={{ color }} /> : <ArrowDownOutlined style={{ color }} />}
        <Text style={{ color }}>{Math.abs(numericPercent)}%</Text>
      </Space>
    )
  }

  const renderMetricBox = (key, config) => {
    let value = 0
    let percentChange = 0
    let trend = 'up'

    // Get value from metrics based on key
    if (metrics && metrics[key]) {
      if (key === 'utilizationRate') {
        value = metrics[key].daily?.rate || 0
        percentChange = metrics[key].daily?.percentChange || 0
      } else if (key === 'virtualConsults') {
        value = metrics[key].daily?.rate || 0
        percentChange = metrics[key].daily?.percentChange || 0
      } else if (key === 'cancellations') {
        value = metrics[key].daily?.count || 0
        percentChange = metrics[key].daily?.percentChange || 0
      } else if (key === 'unclaimed') {
        value = metrics[key].count || 0
        percentChange = metrics[key].percentChange || 0
      } else if (key === 'scheduled') {
        value = metrics[key].count || 0
        percentChange = metrics[key].percentChange || 0
      } else if (key === 'nonScheduled') {
        value = metrics[key].count || 0
        percentChange = metrics[key].percentChange || 0
      } else {
        value = metrics[key].count || 0
        percentChange = metrics[key].percentChange || 0
      }
      trend = percentChange >= 0 ? 'up' : 'down'
    }

    // Format the value based on showDecimals setting
    const formattedValue = config.showDecimals ? value.toFixed(1) : Math.round(value)

    // Add flicker animation if unclaimed metric is greater than 0
    const shouldFlicker = key === 'unclaimed' && value > 0 && config.flicker
    const cardStyle = {
      height: '100%',
      animation: shouldFlicker ? 'flickerAnimation 1.5s infinite' : 'none',
      boxShadow: shouldFlicker ? '0 0 8px rgba(245, 34, 45, 0.5)' : 'none',
      transition: 'all 0.3s ease'
    }

    return (
      <Card size='small' bodyStyle={{ padding: '12px' }} style={cardStyle}>
        <style jsx>{`
          @keyframes flickerAnimation {
            0% {
              opacity: 1;
              box-shadow: 0 0 8px rgba(245, 34, 45, 0.5);
              transform: scale(1);
            }
            50% {
              opacity: 0.5;
              box-shadow: 0 0 15px rgba(245, 34, 45, 0.8);
              transform: scale(1.02);
            }
            100% {
              opacity: 1;
              box-shadow: 0 0 8px rgba(245, 34, 45, 0.5);
              transform: scale(1);
            }
          }
        `}</style>
        <Space direction='horizontal' size='middle' align='start'>
          <div>{config.icon}</div>
          <Space direction='vertical' size={0}>
            <Title level={3} style={{ margin: 0, color: config.color }}>
              {formattedValue}
              {config.suffix}
            </Title>
            <Text type='secondary'>{config.label}</Text>
            {renderTrendIndicator(trend, percentChange.toFixed(1))}
          </Space>
        </Space>
      </Card>
    )
  }

  const authState = useSelector((state) => state.auth)
  const [openingApp, setOpeningApp] = useState(false)

  const handleStartConsult = async (record, skipScreening) => {
    let mainMemberProfile = null
    let workflowHistory = {}
    let processRecord = null
    let newRecord = null

    setOpeningApp(true)

    try {
      if (skipScreening) {
        workflowHistory = await getBookingState([record.processRef])
        processRecord = await executeBooking(
          'patient_journey',
          record.processRef,
          'submit',
          `${authState.agiliteUser.firstName} ${authState.agiliteUser.lastName}`,
          workflowHistory.key,
          'consultation'
        )
        newRecord = await updateBooking(
          { status: record.clinicRecord.bpmConfig?.find((e) => e.key === processRecord.key)?.key },
          { _id: record._id }
        )
        workflowHistory = processRecord
        workflowHistory.history.splice(0, 1)
        updateAllBookingsState(newRecord)
      } else {
        if (record.processRef) {
          workflowHistory = await getBookingState([record.processRef])
          workflowHistory.history.splice(0, 1)
        }
      }
      if (record.patientRecord?.mainMemberId) {
        mainMemberProfile = await readSystemUsersAggregate({ _id: record.patientRecord.mainMemberId })
      }
      dispatch(
        coreReducer.actions.addTab({
          key: `${CoreEnums.tabKeys.MY_BOOKINGS}_${record._id}`,
          closable: true,
          label: `Booking: ${record.patientName}`,
          children: (
            <PatientExaminationForm
              data={{
                ...record,
                ...newRecord,
                mainMemberProfile: mainMemberProfile ? mainMemberProfile[0] : undefined
              }}
              refreshView={refreshView}
              workflowHistory={workflowHistory}
            />
          )
        })
      )
    } catch (e) {
      message.error(handleError(e, true))
    }
    setOpeningApp(false)
  }

  const claimBooking = async (record) => {
    try {
      const updatedBooking = await updateBooking({ medicalProfRef: authState.agiliteUser._id }, { _id: record._id })
      updatedBooking.medicalProfName = `${authState.agiliteUser.firstName} ${authState.agiliteUser.lastName}`
      updateAllBookingsState(updatedBooking)
    } catch (e) {
      message.error(handleError(e, true))
    }
  }

  // const clinicServiceState = useSelector((state) => state.services.data)
  // const virtualServiceState = useSelector((state) => state.virtualServices.data)

  // const getService = (service) => {
  //   const tmpService =
  //     clinicServiceState.find((s) => s._id === service) || virtualServiceState.find((s) => s._id === service)
  //   if (tmpService) {
  //     return tmpService?.name || 'Unknown service'
  //   } else {
  //     return 'Unknown service'
  //   }
  // }

  const { token } = theme.useToken()
  const renderAppointmentItem = (appointment) => {
    return (
      <List.Item>
        <div style={{ width: '100%' }}>
          <Space wrap style={{ gap: 16, width: '100%', justifyContent: 'space-between' }}>
            <Space style={{ gap: 16 }}>
              <Space direction='vertical' size={0}>
                <Space>
                  <ClockCircleOutlined />
                  <Text>{appointment.startTime ? appointment.startTime : 'N/A'}</Text>
                </Space>
                {generateStatus(appointment, appointment.status, token, appointment.wasNoShow)}
              </Space>
              <Space size='middle'>
                <Avatar
                  size={40}
                  src={appointment.patientRecord?.profileImage}
                  style={{ backgroundColor: getAvatarColor(appointment.patientRecord?.gender) }}
                >
                  {appointment.patientRecord?.firstName?.[0]}
                  {appointment.patientRecord?.lastName?.[0]}
                </Avatar>
                <Space direction='vertical' size={0}>
                  <Space>
                    <Text strong>
                      {appointment.patientRecord?.firstName && appointment.patientRecord?.lastName
                        ? `${appointment.patientRecord.firstName} ${appointment.patientRecord.lastName}`
                        : appointment.patientName || 'Unknown Patient'}
                    </Text>
                    <Text type='secondary'>{`${
                      appointment.patientRecord?.gender
                        ? appointment.patientRecord.gender.charAt(0).toUpperCase() +
                          appointment.patientRecord.gender.slice(1)
                        : 'Unknown'
                    }, ${calculateAge(appointment.patientRecord?.dateOfBirth)}`}</Text>
                  </Space>
                  {appointment.medicalProfRef ? (
                    <Text style={{ color: token.colorPrimary, fontWeight: 500 }}>
                      Dr. {appointment.professionalName}
                    </Text>
                  ) : (
                    <Text style={{ color: token.colorWarning, fontWeight: 500 }}>Unclaimed</Text>
                  )}
                  <Space>
                    {/* <Text type='secondary'>{getService(appointment.service)}</Text> */}
                    {appointment.isVirtualVisit && <VideoCameraOutlined />}
                  </Space>
                </Space>
              </Space>
            </Space>

            {(isDoctor() || isNurse()) && (
              <>
                {!appointment.medicalProfRef ? (
                  <Button
                    style={{
                      justifySelf: 'right',
                      background: hexToRGBA(token.colorSuccess, 0.1),
                      borderColor: token.colorSuccess,
                      color: token.colorSuccess,
                      fontWeight: 'bold',
                      marginRight: '16px'
                    }}
                    icon={<FontAwesomeIcon icon={faUserCheck} />}
                    onClick={() => claimBooking(appointment)}
                  >
                    Claim
                  </Button>
                ) : (
                  <Button
                    style={{
                      justifySelf: 'right',
                      background: hexToRGBA(token.colorSuccess, 0.1),
                      borderColor: token.colorSuccess,
                      color: token.colorSuccess,
                      fontWeight: 'bold',
                      marginRight: '16px'
                    }}
                    icon={
                      appointment.isVirtualVisit ? (
                        <VideoCameraOutlined style={{ color: token.colorSuccess, fontSize: '16px' }} />
                      ) : (
                        <PlayCircleOutlined style={{ color: token.colorSuccess, fontSize: '16px' }} />
                      )
                    }
                    onClick={() => handleStartConsult(appointment)}
                  >
                    Start Consult
                  </Button>
                )}
              </>
            )}
          </Space>
        </div>
      </List.Item>
    )
  }

  const handleAIAction = (action) => {
    if (action === 'openLetterModal') {
      const newLetterData = {
        title: 'Sample Letter',
        recipient: 'Sample Recipient',
        date: new Date().toLocaleDateString(),
        subject: 'Sample Subject',
        content:
          'This is a sample letter content.\n\nPlease use voice commands to generate a real letter with the AI assistant.',
        signature: 'Dr. Alice'
      }
      setLetterData(newLetterData)
      setIsModalVisible(true)
    }
  }

  const handleAIWarning = (warning) => {}

  const handleModalClose = () => {
    setIsModalVisible(false)
    setLetterData(null)
  }

  const renderAppointmentSkeleton = () => {
    return (
      <List.Item style={{ padding: '16px 0', borderBottom: '1px solid #f0f0f0' }}>
        <Row style={{ width: '100%' }} align='middle' justify='space-between' wrap={false}>
          <Col span={24}>
            <Space style={{ gap: 16, justifyContent: 'space-between', width: '100%' }}>
              <Space style={{ gap: 16 }}>
                <Space direction='vertical' size={4} style={{ maxWidth: '200px' }}>
                  <Skeleton.Input active size='small' style={{ width: '100%' }} />
                  <Skeleton.Input active size='small' style={{ width: '80%' }} />
                </Space>
                <Space>
                  <Skeleton.Avatar active size={40} style={{ backgroundColor: '#E6F7FF' }} />
                  <Space direction='vertical' size={4} style={{ maxWidth: '200px' }}>
                    <Skeleton.Input active size='small' style={{ width: '100%' }} />
                    <Skeleton.Input active size='small' style={{ width: '80%' }} />
                  </Space>
                </Space>
              </Space>
              <Space>
                <Skeleton.Button active size='small' style={{ width: 120 }} />
              </Space>
            </Space>
          </Col>
        </Row>
      </List.Item>
    )
  }

  const renderMetricSkeleton = () => {
    return (
      <Card size='small' bodyStyle={{ padding: '12px' }} style={{ height: '100%' }}>
        <Space direction='horizontal' size='middle' align='start'>
          <Skeleton.Avatar active size='small' />
          <Space direction='vertical' size={0}>
            <Skeleton.Button active size='small' style={{ width: 80 }} />
            <Skeleton.Button active size='small' style={{ width: 60 }} />
            <Skeleton.Button active size='small' style={{ width: 40 }} />
          </Space>
        </Space>
      </Card>
    )
  }

  const AppointmentOverviewAssistant = ({ appointments, doctorName, onStartConsult }) => {
    // Get the next appointment (first in the upcoming list)
    const nextAppointment = appointments && appointments.length > 0 ? appointments[0] : null

    // Format appointment details for display in messages
    const formatAppointmentDetails = useCallback((appointment) => {
      if (!appointment) return 'No appointments scheduled'

      return `Patient: ${
        appointment.patientRecord?.firstName && appointment.patientRecord?.lastName
          ? `${appointment.patientRecord.firstName} ${appointment.patientRecord.lastName}`
          : appointment.patientName || 'Unknown Patient'
      }
  Time: ${appointment.startTime}
  Type: ${appointment.isVirtualVisit ? 'Virtual' : 'In-person'}`
    }, [])

    // Handle actions from the assistant
    const handleAction = useCallback(
      (action) => {
        // Add logging to debug

        switch (action) {
          case 'showWelcomeMessage':
            message.info('Hello Doctor! I can help you with your appointments.')
            break
          case 'summarizeAppointments':
            const count = appointments?.length || 0
            message.info(`You have ${count} appointments scheduled.`)
            break
          case 'startNextAppointment':
            if (nextAppointment) {
              message.info(
                `Starting consultation with ${
                  nextAppointment.patientRecord?.firstName && nextAppointment.patientRecord?.lastName
                    ? `${nextAppointment.patientRecord.firstName} ${nextAppointment.patientRecord.lastName}`
                    : nextAppointment.patientName || 'Unknown Patient'
                }`
              )
              onStartConsult(nextAppointment)
            } else {
              message.info('No upcoming appointments to start')
            }
            break
          case 'showNextAppointment':
            if (nextAppointment) {
              message.info(`Next appointment: ${formatAppointmentDetails(nextAppointment)}`)
            } else {
              message.info('No upcoming appointments scheduled')
            }
            break
          case 'openLetterModal':
            message.info('Letter modal would open here')
            break
          default:
        }
      },
      [appointments, nextAppointment, onStartConsult, formatAppointmentDetails]
    )

    // Action triggers including new appointment-related actions
    const actionTriggers = [
      {
        phrases: [
          'welcome message',
          'say hello',
          'greet me',
          'show welcome',
          'display welcome message',
          'hello doctor'
        ],
        action: 'showWelcomeMessage'
      },
      {
        phrases: [
          'how many appointments',
          'appointment count',
          'summarize appointments',
          'count my appointments',
          'appointment summary'
        ],
        action: 'summarizeAppointments'
      },
      {
        phrases: [
          'start next appointment',
          'start consultation',
          'begin next appointment',
          'start next consult',
          'start appointment',
          'open next appointment'
        ],
        action: 'startNextAppointment'
      },
      {
        phrases: [
          'show next appointment',
          'what is my next appointment',
          'next patient details',
          'who is next',
          'next appointment details'
        ],
        action: 'showNextAppointment'
      }
    ]

    // Enhanced context with appointment starting capability
    const appointmentOverviewContext = `You are a helpful AI Assistant for the appointment overview section.
  
  ROLE: You are assisting the doctor with their appointment overview.
  
  APPOINTMENT INFORMATION:
  - The doctor has ${appointments?.length || 0} appointments scheduled
  ${
    nextAppointment
      ? `- Next appointment: ${
          nextAppointment.patientRecord?.firstName && nextAppointment.patientRecord?.lastName
            ? `${nextAppointment.patientRecord.firstName} ${nextAppointment.patientRecord.lastName}`
            : nextAppointment.patientName || 'Unknown Patient'
        } at ${nextAppointment.startTime || 'flexible time'}`
      : '- No upcoming appointments'
  }
  - You can help summarize appointment information
  - You can assist with navigating the dashboard
  
  CAPABILITIES:
  - Summarize appointment information (say "summarize appointments")
  - Show welcome message (say "welcome message")
  - Start the next appointment (say "start next appointment")
  - Show details of the next appointment (say "show next appointment")
  - Help with letter dictation (say "open letter modal" to start)
  
  IMPORTANT TRIGGER PHRASES:
  When the user asks for a welcome message or greeting, respond with exactly "welcome message" in your reply.
  When the user asks about appointment counts, include "summarize appointments" in your reply.
  When the user asks to start or begin the next appointment, include "start next appointment" in your reply.
  When the user asks about who their next patient is, include "show next appointment" in your reply.
  
  INITIAL GREETING:
  Only say "Hello Doctor! I can help you with your appointment overview. What would you like to know?"
  
  INTERACTION GUIDELINES:
  - Keep responses focused on appointments
  - Be concise and helpful
  - Offer to summarize appointments if asked
  - When asked to start an appointment, include the exact phrase "start next appointment" in your response
  - When asked about the next patient, include the exact phrase "show next appointment" in your response
  
  Remember: Always wait for explicit questions before providing information.`

    return (
      <WebRTCAssistant
        systemContext={appointmentOverviewContext}
        onAction={handleAction}
        assistantName='Appointment Overview Assistant'
        actionTriggers={actionTriggers}
        buttonText='Start AI Assistant'
        buttonProps={{
          size: 'middle',
          style: {
            background: 'linear-gradient(90deg, #4169E1, #E091A9, #C71585)',
            borderColor: '#E091A9',
            color: 'white',
            fontWeight: 'bold'
          }
        }}
        containerStyle={{
          marginBottom: '20px'
        }}
      />
    )
  }

  return (
    <Card bodyStyle={{ padding: '16px' }}>
      <Space direction='vertical' size='middle' style={{ width: '100%' }}>
        {/* Header Section */}
        <Row justify='space-between' align='middle'>
          <Col>
            <Space direction='vertical' size={0}>
              <Title level={2} style={{ margin: 0, color: '#1890ff' }}>
                Hello,{' '}
                {authState.agiliteUser.extraData.role.type === 'medical_professional'
                  ? isNurse()
                    ? 'Nurse '
                    : 'Dr. '
                  : ''}
                {authState.agiliteUser.lastName}
              </Title>
              <Text type='secondary'>
                {isDoctor() || isNurse()
                  ? 'See the person who needs your help!'
                  : 'See the person who needs your help!'}
              </Text>
            </Space>
          </Col>
          <Col>
            <Space size='middle'>
              {/* <AppointmentOverviewAssistant
                appointments={summaryData.upcoming}
                doctorName={authState.agiliteUser.firstName}
                onStartConsult={handleStartConsult}
              /> */}
              <NotificationsPopover />
              <Avatar
                size={40}
                src={authState.agiliteUser.profileImage || '/assets/images/doctor-profile.jpg'}
                style={{
                  backgroundColor: authState.agiliteUser.profileImage ? 'transparent' : '#1890ff',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'
                }}
              >
                {!authState.agiliteUser.profileImage &&
                  `${authState.agiliteUser.firstName?.[0]}${authState.agiliteUser.lastName?.[0]}`}
              </Avatar>
            </Space>
          </Col>
        </Row>

        {/* Appointments Section */}
        <Card bodyStyle={{ padding: '12px' }}>
          <Space direction='vertical' style={{ width: '100%' }} size='small'>
            <Row justify='space-between' align='middle'>
              <Col>
                <Title level={4} style={{ margin: 0 }}>
                  Today's Appointments
                </Title>
              </Col>
              <Col>
                <Space>
                  <Input.Search
                    placeholder='Search patient...'
                    onChange={(e) => setSearchTerm(e.target.value)}
                    style={{ width: 250 }}
                    allowClear
                  />
                  <Button
                    onClick={() => {
                      refreshView()
                      refreshMetrics()
                    }}
                    style={{
                      justifySelf: 'right',
                      background: hexToRGBA(token.colorPrimary, 0.1),
                      borderColor: token.colorPrimary,
                      color: token.colorPrimary,
                      fontWeight: 'bold'
                    }}
                    icon={<FontAwesomeIcon icon={faSync} style={{ color: token.colorPrimary, fontSize: '16px' }} />}
                  >
                    Sync
                  </Button>
                </Space>
              </Col>
            </Row>
            {loadingApps ? (
              <List style={{ width: '100%' }}>
                {[1, 2].map((key) => (
                  <React.Fragment key={key}>{renderAppointmentSkeleton()}</React.Fragment>
                ))}
              </List>
            ) : (
              <List
                dataSource={summaryData.upcoming}
                renderItem={renderAppointmentItem}
                style={{ maxHeight: '500px', overflowY: 'auto', paddingRight: '4px' }}
              />
            )}
          </Space>
        </Card>

        {/* Metrics Section */}
        <Card bodyStyle={{ padding: '12px' }}>
          <Space direction='vertical' style={{ width: '100%' }} size='small'>
            <Text style={{ fontSize: '18px', margin: 0 }}>Appointments Overview</Text>
            {loadingMetrics ? (
              <Row gutter={[12, 12]}>
                {[1, 2, 3, 4, 5, 6].map((key) => (
                  <Col xs={24} sm={12} xl={8} key={key}>
                    {renderMetricSkeleton()}
                  </Col>
                ))}
              </Row>
            ) : (
              <Row gutter={[12, 12]}>
                {Object.entries(METRIC_CONFIGS).map(([key, config]) => (
                  <Col xs={24} sm={12} xl={8} key={key}>
                    {renderMetricBox(key, config)}
                  </Col>
                ))}
              </Row>
            )}
          </Space>
        </Card>
      </Space>

      <LetterModal isVisible={isModalVisible} onClose={handleModalClose} letterData={letterData} />
    </Card>
  )
}

export default AppointmentOverview
